<template>
    <div>
        <v-container grid-list-xl fluid>
            <v-layout wrap>
                <v-flex xs12 class="d-flex align-center flex-wrap header mr-8">
                    <h1 class="mr-4">Carregamentos via Aplicativo</h1>
                    <v-spacer />
                    <div class="date-filter">
                        <DateInterval @onChange="changeDate" today />
                    </div>
                    <v-btn-toggle v-model="view" mandatory dense class="ml-4 mr-16">
                        <v-btn small :value="VIEWS.UNIFIED" title="Lista unificada"><v-icon small>view_list</v-icon></v-btn>
                        <v-btn small :value="VIEWS.SPLIT" title="Sessões e histórico separados"><v-icon small>view_module</v-icon></v-btn>
                    </v-btn-toggle>
                </v-flex>
                <!-- Filtros em linha própria para o painel aberto usar a largura toda sem empurrar o cabeçalho -->
                <v-flex xs12 class="py-0">
                    <FilterBar v-model="filters" :points="points" />
                </v-flex>
                <v-flex xs12>
                    <v-divider />
                </v-flex>

                <BoardsError class="flex xs12" />

                <v-flex xs12>
                    <DailySummary :sessions="filteredSessions" :chargers="chargers" :plugs="plugs" :history="filteredHistory" :inconsistencies="inconsistencies" :filtered="filtered" />
                </v-flex>

                <v-flex xs12 v-if="view == VIEWS.UNIFIED">
                    <UnifiedArea :charges="charges" :filters="filters" :sessions-refresh="refresh.sessions" :history-refresh="refresh.history" @open-payment="idPayment = $event" />
                </v-flex>

                <template v-else>
                    <v-flex xs12>
                        <SessionsArea :sessions="sessions" :filters="filters" :refresh="refresh.sessions" @open-payment="idPayment = $event" />
                    </v-flex>

                    <v-flex xs12>
                        <HistoryArea :history="history" :filters="filters" :refresh="refresh.history" @open-payment="idPayment = $event" />
                    </v-flex>
                </template>
            </v-layout>
        </v-container>

        <PaymentAction :id="idPayment" @close="idPayment = undefined" />

        <SessionsSource :date-from="dateFrom" :date-to="dateTo" @state="refresh.sessions = $event" @update="updateSessions" />
        <HistorySource :date-from="dateFrom" :date-to="dateTo" @state="refresh.history = $event" @update="updateHistory" />
    </div>
</template>

<script>
import DateInterval from "@/components/core/DateInterval";
import PaymentAction from "@/components/shared/PaymentAction";
import BoardsError from "@/components/shared/BoardsError.vue";

import DailySummary from "./DailySummary";
import FilterBar from "./FilterBar";
import HistoryArea from "./HistoryArea";
import SessionsArea from "./SessionsArea";
import UnifiedArea from "./UnifiedArea";
import { buildCharges } from "./shared/charges";
import { ALERTS, filtersFromQuery, filtersToQuery, hasFilters, loadQuery, matchCharge, matchHistory, matchSession, pointsOf, saveQuery } from "./shared/filters";
import HistorySource from "./sources/HistorySource";
import SessionsSource from "./sources/SessionsSource";

const VIEWS = Object.freeze({ UNIFIED: "unified", SPLIT: "split" });

export default {
    components: { DateInterval, PaymentAction, BoardsError, DailySummary, FilterBar, SessionsArea, HistoryArea, UnifiedArea, SessionsSource, HistorySource },

    data() {
        const query = loadQuery(this.$route.query);
        return {
            todayTimer: null,
            today: "",
            // Sem período selecionado acompanha o dia atual, virando o dia sem recarregar a tela
            selectedDates: null,
            sessions: [],
            chargers: null,
            plugs: null,
            history: [],
            sessionsRequestedAt: null,
            historyRequestedAt: null,
            refresh: { sessions: null, history: null },
            idPayment: undefined,
            filters: filtersFromQuery(query),
            view: query.view == VIEWS.SPLIT ? VIEWS.SPLIT : VIEWS.UNIFIED,
            VIEWS,
        };
    },

    created() {
        this.updateToday();
        // Reflete na URL os filtros restaurados do navegador
        this.updateUrl();
    },

    mounted() {
        // Acompanha a virada do dia quando nenhum período foi selecionado
        this.todayTimer = setInterval(this.updateToday, 60 * 1000);
    },

    beforeDestroy() {
        clearInterval(this.todayTimer);
    },

    methods: {
        updateToday() {
            this.today = this.$moment().subtract({ hours: 3 }).toISOString().substr(0, 10);
        },
        // O DateInterval emite [dia] ou [início, fim], e vazio quando o período passa do limite
        changeDate(dates) {
            if (dates.length == 0) return;
            const [from, to] = [dates[0], dates[1] || dates[0]];
            this.selectedDates = from == this.today && to == this.today ? null : [from, to];
        },
        clearData() {
            // Limpa para não exibir dados de outro período enquanto a nova busca não retorna
            this.sessions = [];
            this.history = [];
            this.sessionsRequestedAt = null;
            this.historyRequestedAt = null;
        },
        updateSessions({ sessions, chargers, plugs, requestedAt }) {
            this.sessions = sessions;
            this.chargers = chargers;
            this.plugs = plugs;
            this.sessionsRequestedAt = requestedAt;
        },
        updateHistory({ history, requestedAt }) {
            this.history = history;
            this.historyRequestedAt = requestedAt;
        },
        updateUrl() {
            // Também salva no navegador para a próxima visita abrir com os mesmos filtros.
            // Atualiza só a URL, sem navegação no router, para não disparar o beforeEach (que reativa o "Aguarde..." na próxima requisição)
            const query = { ...filtersToQuery(this.filters), ...(this.view == VIEWS.SPLIT ? { view: VIEWS.SPLIT } : {}) };
            const url = this.$router.resolve({ path: this.$route.path, query }).href;
            window.history.replaceState(window.history.state, "", url);
            saveQuery(query);
        },
    },

    computed: {
        dateFrom() {
            return this.selectedDates ? this.selectedDates[0] : this.today;
        },
        dateTo() {
            return this.selectedDates ? this.selectedDates[1] : this.today;
        },
        // O resumo respeita local e busca, mas não o filtro de alerta para não distorcer os totais do dia
        summaryFilters() {
            return { ...this.filters, alerts: ALERTS.ALL };
        },
        filtered() {
            return hasFilters(this.summaryFilters);
        },
        filteredSessions() {
            return this.sessions.filter((session) => matchSession(session, this.summaryFilters));
        },
        filteredHistory() {
            return this.history.filter((item) => matchHistory(item, this.summaryFilters));
        },
        charges() {
            return buildCharges(this.sessions, this.history, { sessionsRequestedAt: this.sessionsRequestedAt, historyRequestedAt: this.historyRequestedAt });
        },
        inconsistencies() {
            return this.charges.filter((item) => item.inconsistentHistory && matchCharge(item, this.summaryFilters)).length;
        },
        points() {
            return [...new Set([...pointsOf(this.sessions), ...pointsOf(this.history)])];
        },
    },

    watch: {
        dateFrom() {
            this.clearData();
        },
        dateTo() {
            this.clearData();
        },
        filters() {
            this.updateUrl();
        },
        view() {
            this.updateUrl();
        },
    },
};
</script>

<style scoped>
.header {
    gap: 8px 0;
}

.date-filter {
    width: 230px;
}

.date-filter >>> .v-text-field__details {
    display: none;
}

.date-filter >>> .v-input {
    margin-top: 0;
    padding-top: 0;
}
</style>

<style src="./shared/alert.css"></style>
<style src="./shared/charge-grid.css"></style>
